import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ShoppingCartDto } from 'src/app/core/dtos/dto';
import { ProductTypeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { PostLoginService } from 'src/app/core/services/post-login.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { selectAuth } from 'src/app/state/auth.selectors';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';
import { selectShoppingCart, selectShoppingCartItemCount } from 'src/app/state/shopping-cart.selectors';
import { AffiliateService } from 'src/app/core/services/affiliate.service';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { CheckoutService } from 'src/app/core/services/checkout.service';
import { setRegId, setRegTestSession, setTestPurposeOption } from 'src/app/state/reg-data.actions';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { TestPurposeOption } from 'src/app/core/dtos/test-purpose-option';
import { selectRegTestSession } from 'src/app/state/reg-data.selectors';

@Component({
  selector: 'app-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss']
})
export class ShoppingCartComponent implements OnInit, OnDestroy {

  shoppingCart$: Observable<ShoppingCartDto>;
  shoppingCartItemCount$ = this.store.pipe(select(selectShoppingCartItemCount));
  loadCartSubscription: Subscription;
  removeCartSubscription: Subscription;
  authSubscription: Subscription;
  testSessionInformationSubscription: Subscription;
  hasReg: boolean;
  isBusinessAccount: boolean;
  businessAccountSubscription: Subscription;
  shoppingCart: ShoppingCartDto;
  cartIsReady: boolean;

  constructor(
    private store: Store,
    private shoppingCartService: ShoppingCartService,
    private toastService: HotToastService,
    private postLoginService: PostLoginService,
    private affiliateService: AffiliateService,
    private router: Router,
    private checkoutService: CheckoutService,
    private registrationService: RegistrationService
  ) { }

  ngOnInit(): void {
    this.affiliateService.checkAndInitAffiliateTracking();
    this.shoppingCart$ = this.store.pipe(select(selectShoppingCart));
    this.checkBusinessAccount();
    this.loadCartSubscription = this.shoppingCartService.getShoppingCart()
      .subscribe(
        result => {
          this.shoppingCart = result.content;
          this.store.dispatch(loadShoppingCart({ shoppingCart: result.content }));
          this.checkIfCartContainsReg(result.content);
          this.cartIsReady = true;
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning("Unable to load shopping cart");
            }
          }
        });
    this.authSubscription = this.store.select(selectAuth).pipe(take(1))
      .subscribe(result => {
        if (!result) {
          this.postLoginService.showLoginRedirect();
        }
      });
  }

  removeFromCart(id: number) {
    this.removeCartSubscription = this.shoppingCartService.removeShoppingCartItem(id)
      .subscribe(
        result => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: result.content }));
          this.checkIfCartContainsReg(result.content);
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning("Unable to remove item from shopping cart");
            }
          }
        });
  }

  checkoutRedirect() {
    if (this.hasReg && !this.isBusinessAccount) {
      this.CheckStoreRegData();
    } else {
      this.router.navigate(['checkout/address']);
    }
  }

  CheckStoreRegData() {
    this.testSessionInformationSubscription = this.store.pipe(select(selectRegTestSession))
      .subscribe(sessionInfo => {
        if (sessionInfo === null) {
          //fill in store again with values from backend if reg got cleared from store
          const shoppingCartRegItem = this.shoppingCart.shoppingCartItems.find(e => e.productTypeId === ProductTypeEnum.TestRegistration);
          forkJoin([this.checkoutService.getShoppingCartReg(shoppingCartRegItem.userAccountId), this.checkoutService.getShoppingCartRegTestPurpose(shoppingCartRegItem.userAccountId)])
            .subscribe(
              checkoutServiceResult => {
                this.registrationService.getTestSessionInfo(checkoutServiceResult[0].content.siteCode, checkoutServiceResult[0].content.testTypeId)
                  .subscribe(testSession => {
                    const testSessionReg = testSession.content;
                    this.store.dispatch(setRegTestSession({ testSession: testSessionReg }));
                    this.store.dispatch(setRegId({ regId: checkoutServiceResult[0].content.regId }));
                    const testPurpose: TestPurposeOption = {
                      id: checkoutServiceResult[1].content.id.toString(),
                      parentId: null,
                      testPurposeId: checkoutServiceResult[1].content.id,
                      institutionId: null,
                      name: checkoutServiceResult[1].content.description,
                      children: null
                    };
                    this.store.dispatch(setTestPurposeOption({ testPurposeOption: testPurpose }));
                    this.router.navigate(['registration/order-summary']);
                  });
              },
              () => this.toastService.warning("Unable to load page")
            );
        }
        else {
          this.router.navigate(['registration/order-summary']);
        }
      });
  }

  checkIfCartContainsReg(cart: ShoppingCartDto) {
    if (cart.shoppingCartItems.some(e => e.productTypeId === ProductTypeEnum.TestRegistration)) {
      this.hasReg = true;
    } else {
      this.hasReg = false;
    }
  }

  checkBusinessAccount() {
    this.businessAccountSubscription = this.store.select(selectIsBusinessAccount).pipe(take(1))
      .subscribe(
        (result) => {
          this.isBusinessAccount = result;
        }
      );
  }

  ngOnDestroy(): void {
    this.loadCartSubscription?.unsubscribe();
    this.removeCartSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.businessAccountSubscription?.unsubscribe();
    this.testSessionInformationSubscription?.unsubscribe();
  }

}
